import { useEffect, useState } from "react";
import api from "@/lib/api";
import MarketingNav from "@/components/brand/MarketingNav";
import MarketingFooter from "@/components/brand/MarketingFooter";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { reportStripeCheckoutFailure } from "@/sentry/reporters";

// Static fallback so the cards NEVER render as blank, even if the API is briefly
// unreachable. The server-of-record is /api/pricing; this snapshot only kicks in
// on network failure so the customer always sees the plans.
const STATIC_TIERS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 49,
    summary: "For creators, freelancers, and small stores starting their organic engine.",
    features: [
      "1 project",
      "1,000 AI credits / month (managed AI, no key required)",
      "20 AI articles / month",
      "20 regenerations / month",
      "100 keywords tracked",
      "40 AI visibility checks / month",
      "4 SEO audits / month",
      "300 pages scanned / month",
      "3 active automations",
      "1 concurrent AI job",
      "1 team member",
      "Prepaid only — no surprise overage bills",
    ],
    badge: null,
    lookup_key: "trendrise_starter_monthly",
    cta_label: "Start with Starter",
  },
  {
    id: "growth",
    name: "Growth",
    monthly: 149,
    summary: "Serious SEO automation for growing brands and stores.",
    features: [
      "3 projects",
      "3,500 AI credits / month (managed AI, no key required)",
      "80 AI articles / month",
      "80 regenerations / month",
      "1,000 keywords tracked",
      "200 AI visibility checks / month",
      "20 SEO audits / month",
      "5,000 pages scanned / month",
      "10 active automations",
      "2 concurrent AI jobs",
      "3 team members",
      "Prepaid only — no surprise overage bills",
    ],
    badge: "Most popular",
    lookup_key: "trendrise_growth_monthly",
    cta_label: "Start Growth Autopilot",
  },
  {
    id: "scale",
    name: "Scale",
    monthly: 399,
    summary: "Content engine + authority engine combined.",
    features: [
      "10 projects",
      "11,000 AI credits / month (managed AI, no key required)",
      "300 AI articles / month",
      "300 regenerations / month",
      "5,000 keywords tracked",
      "800 AI visibility checks / month",
      "60 SEO audits / month",
      "25,000 pages scanned / month",
      "30 active automations",
      "5 concurrent AI jobs",
      "10 team members",
      "Prepaid only — no surprise overage bills",
    ],
    badge: null,
    lookup_key: "trendrise_scale_monthly",
    cta_label: "Start Scale",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    monthly: 999,
    summary: "Full automation, team roles, white-label reports, API access.",
    features: [
      "25 projects",
      "28,000 AI credits / month (managed AI, no key required)",
      "500 AI articles / month",
      "500 regenerations / month",
      "10,000 keywords tracked",
      "5,000 AI visibility checks / month",
      "150 SEO audits / month",
      "100,000 pages scanned / month",
      "100 active automations",
      "10 concurrent AI jobs",
      "25 team members",
      "Prepaid only — no surprise overage bills",
    ],
    badge: "Enterprise",
    lookup_key: "trendrise_enterprise_monthly",
    cta_label: "Start Enterprise",
  },
];

const STATIC_PACKS = [
  { id: "pack_500", credits: 500, price_usd: 10.0 },
  { id: "pack_2000", credits: 2000, price_usd: 39.0 },
  { id: "pack_10000", credits: 10000, price_usd: 179.0 },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Start with the static snapshot so the cards render immediately (SSR-safe,
  // static-preview-safe, zero flash-of-empty).
  const [tiers, setTiers] = useState(STATIC_TIERS);
  const [packs, setPacks] = useState(STATIC_PACKS);
  const [apiState, setApiState] = useState("static"); // static | live | error
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/pricing")
      .then(({ data }) => {
        if (cancelled) return;
        if (Array.isArray(data?.tiers) && data.tiers.length >= 4) {
          setTiers(data.tiers);
          setPacks(data.credit_packs || STATIC_PACKS);
          setApiState("live");
        } else {
          setApiState("error");
        }
      })
      .catch(() => {
        if (!cancelled) setApiState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startCheckout = async (tier) => {
    if (!user) {
      toast.message("Create an account first to start checkout.");
      navigate("/signup");
      return;
    }
    if (!tier.lookup_key) {
      toast.error("Plan not available");
      return;
    }
    setBusy(tier.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      reportStripeCheckoutFailure(err, { user_id: user?.id, workflow: "checkout_subscription" });
      toast.error(err?.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="dark-marketing grain min-h-screen" data-testid="pricing-page">
      <MarketingNav />
      <section className="pt-36 pb-12 px-6 sm:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <p className="editorial-overline">Pricing</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory-50 mt-5 leading-[1.05]">
            Built to compound.<br />Priced to start today.
          </h1>
          <p className="mt-5 text-ivory-200/70 max-w-2xl mx-auto">
            Every plan ships with the universal LLM key, adaptive dashboards, and the full growth-plan engine. Upgrade as your revenue grows.
          </p>
          <p className="mt-3 text-xs uppercase tracking-widest text-gold-500/80" data-testid="stripe-mode-badge">
            Managed AI · no LLM keys required · Stripe test-mode checkout
          </p>
          {apiState === "error" && (
            <p className="mt-4 inline-block px-3 py-1.5 text-xs text-amber-300 border border-amber-500/40 rounded-sm" data-testid="pricing-api-error">
              Live pricing unavailable — showing the current published plans.
            </p>
          )}
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-24 pb-16">
        <div
          className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          data-testid="pricing-cards-grid"
        >
          {tiers.map((t) => (
            <article
              key={t.id}
              data-testid={`pricing-tier-${t.id}`}
              className={`relative rounded-sm p-7 border flex flex-col ${
                t.badge === "Most popular"
                  ? "border-gold-500 bg-white/[0.04]"
                  : "border-ivory-200/10 bg-white/[0.02]"
              }`}
            >
              {t.badge && (
                <span
                  className="absolute -top-3 right-5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] bg-gold-500 text-navy-900 rounded-sm font-semibold"
                  data-testid={`tier-badge-${t.id}`}
                >
                  {t.badge}
                </span>
              )}
              <h3 className="font-serif text-2xl text-ivory-50">{t.name}</h3>
              <p className="text-xs text-ivory-200/60 mt-2 min-h-[40px]">{t.summary}</p>
              <p className="mt-6">
                <span className="font-serif text-5xl text-ivory-50" data-testid={`tier-price-${t.id}`}>
                  ${t.monthly}
                </span>
                <span className="text-ivory-200/60 text-sm ml-1">/ month</span>
              </p>
              <button
                type="button"
                onClick={() => startCheckout(t)}
                disabled={busy === t.id}
                className={`mt-6 block w-full text-center px-5 py-3 rounded-sm text-sm font-semibold transition-colors disabled:opacity-50 ${
                  t.badge === "Most popular"
                    ? "bg-gold-500 hover:bg-gold-400 text-navy-900"
                    : "border border-ivory-200/20 hover:border-gold-500 text-ivory-50 hover:text-gold-500"
                }`}
                data-testid={`tier-cta-${t.id}`}
              >
                {busy === t.id ? "Redirecting…" : (t.cta_label || "Choose plan")}
              </button>
              {t.id === "enterprise" && (
                <p className="mt-2 text-[11px] text-ivory-200/50 text-center">
                  Or{" "}
                  <Link to="/signup" className="underline hover:text-gold-500" data-testid="contact-sales-link">
                    contact sales
                  </Link>{" "}
                  for onboarding help.
                </p>
              )}
              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="text-sm text-ivory-200/80 flex items-start gap-2">
                    <span className="text-gold-500 mt-0.5">▸</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div
          className="mt-12 max-w-3xl mx-auto border border-ivory-200/10 rounded-sm p-6 bg-white/[0.02]"
          data-testid="prepaid-promise-panel"
        >
          <p className="editorial-overline">Prepaid usage · every plan</p>
          <p className="text-ivory-50 font-serif text-2xl mt-2" data-testid="no-overage-promise">
            No surprise overage bills. When you reach your included usage, upgrade or add prepaid credits.
          </p>
          <p className="text-sm text-ivory-200/70 mt-3 leading-relaxed">
            Included monthly credits reset every billing cycle. Optional prepaid credit packs are paid up front,
            never expire for 12 months, and are only used after your included credits. Nothing is billed after the fact.
          </p>
          <ul className="mt-4 grid sm:grid-cols-3 gap-3 text-sm text-ivory-200/80">
            {packs.map((o) => (
              <li key={o.id} className="border border-ivory-200/10 rounded-sm p-3" data-testid={`pricing-pack-${o.id}`}>
                <p className="text-ivory-50 font-semibold">{o.credits.toLocaleString()} credits</p>
                <p className="text-xs text-ivory-200/50 mt-1">${Number(o.price_usd).toFixed(0)} · one-time, prepaid</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
